import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AppService } from '../../services/app.service';
import { PowerBIEmbedConfig, PowerBIEmbedService, RoleDatasetConfig } from '../../services/powerbi-embed.service';
import { PowerBIMenuService } from '../../services/powerbi-menu.service';

@Component({
  selector: 'app-powerbi-dashboard',
  templateUrl: './powerbi-dashboard.component.html',
  styleUrls: ['./powerbi-dashboard.component.scss']
})
export class PowerBiDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('reportContainer', { static: true }) reportContainer!: ElementRef<HTMLDivElement>;

  @HostListener('window:resize')
  onResize(): void {
    if (this.report) {
      try {
        const powerbiClient = (window as any)['powerbi-client'];
        const models = powerbiClient?.models;
        if (models) {
          this.report.updateSettings({
            customLayout: {
              displayOption: models.DisplayOption.FitToPage
            }
          }).catch(() => {});
        }
      } catch (e) {}
    }
  }

  isLoading = true;
  embedConfig?: PowerBIEmbedConfig;
  report: any;
  tenantDomain = localStorage.getItem('tenantDomain') || '';
  currentYear = new Date().getFullYear();

  // Role-based Active Dashboard State & Error Handling
  activeDashboardType = 'Operational';
  hasPermissionError = false;
  permissionMessage = '';

  // Admin Power BI Settings Modal State
  showAdminSettingsModal = false;
  isLoadingRolesConfig = false;
  isSavingRolesConfig = false;
  rolesConfigList: RoleDatasetConfig[] = [];

  // Refresh Schedule Modal Variables
  showRefreshModal = false;
  isEditingSchedule = false;
  notifyOthersEnabled = false;
  customEmails = '';
  refreshFrequency: 'Daily' | 'Weekly' = 'Daily';
  allDays: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  scheduleData: any = {
    days: [] as string[],
    times: [] as string[],
    localTimeZoneId: 'UTC',
    enabled: false,
    notifyOption: 'NoNotification'
  };
  backupScheduleData: any = {};
  timeSlots: Array<{ hour: string; minute: string; period: string }> = [];

  private subscriptions: Subscription[] = [];

  constructor(
    private powerBIEmbedService: PowerBIEmbedService,
    private powerBiMenuService: PowerBIMenuService,
    private appService: AppService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadReport();

    this.subscriptions.push(
      this.powerBiMenuService.selectDashboard$.subscribe((dashboardType) => {
        if (dashboardType && dashboardType !== this.activeDashboardType) {
          this.activeDashboardType = dashboardType;
          this.loadReport();
        }
      }),
      this.powerBiMenuService.openPowerBiSettings$.subscribe(() => {
        this.openAdminSettingsModal();
      }),
      this.powerBiMenuService.exportReport$.subscribe(() => {
        this.exportReport();
      }),
      this.powerBiMenuService.openRefreshSchedule$.subscribe(() => {
        this.openRefreshSchedule();
      }),
      this.powerBiMenuService.downloadData$.subscribe(() => {
        this.downloadData();
      })
    );
  }

  ngOnDestroy(): void {
    this.resetPowerBIContainer();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadReport(): void {
    this.isLoading = true;
    this.hasPermissionError = false;
    this.permissionMessage = '';
    this.resetPowerBIContainer();

    this.powerBIEmbedService.getEmbedConfig(this.activeDashboardType).subscribe({
      next: async (config) => {
        this.embedConfig = config;
        this.tenantDomain = config.tenantDomain || this.tenantDomain;
        this.powerBiMenuService.setTenantDomain(this.tenantDomain);

        console.log(`[Smart Poultry] ${this.activeDashboardType} Dashboard Dataset:`, config.datasetId);

        try {
          await this.embedPowerBIReport(config);
          this.toastr.success(`${this.activeDashboardType} Dashboard loaded`);
        } catch (error: any) {
          console.error(error);
          this.toastr.error(error?.message || 'Could not embed Power BI report');
        } finally {
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.hasPermissionError = true;
        const errMsg = typeof error?.error === 'string'
          ? error.error
          : error?.error?.message || 'You are not allowed to see this dashboard or the dashboard is not available for your tenant, please contact with admin.';
        
        this.permissionMessage = errMsg;
        console.warn('[Smart Poultry] Access Restricted:', errMsg);
      }
    });
  }

  async exportReport(): Promise<void> {
    if (!this.report) {
      this.toastr.warning('Report is still loading or not available.');
      return;
    }

    try {
      await this.report.print();
      this.toastr.success('Export dialog opened');
    } catch (error) {
      console.error(error);
      this.toastr.error('Export failed. Check Power BI embed permissions.');
    }
  }

  hoursList: string[] = ['01','02','03','04','05','06','07','08','09','10','11','12'];
  minutesList: string[] = ['00', '30'];

  convertStringToSlot(timeStr: string) {
    if (!timeStr || !timeStr.includes(':')) {
      return { hour: '06', minute: '00', period: 'AM' };
    }
    const parts = timeStr.split(':');
    const hh = parseInt(parts[0]);
    const mm = parts[1] || '00';
    
    const period = hh >= 12 ? 'PM' : 'AM';
    let hr = hh % 12;
    if (hr === 0) {
      hr = 12;
    }
    
    const hourStr = hr.toString().padStart(2, '0');
    return { hour: hourStr, minute: mm, period: period };
  }

  convertSlotToString(slot: { hour: string; minute: string; period: string }): string {
    const hr = parseInt(slot.hour);
    let hh = hr;
    
    if (slot.period === 'AM') {
      if (hh === 12) {
        hh = 0;
      }
    } else { // PM
      if (hh !== 12) {
        hh += 12;
      }
    }
    
    const hhStr = hh.toString().padStart(2, '0');
    return `${hhStr}:${slot.minute}`;
  }

  formatTimeTo12Hour(time24: string): string {
    const slot = this.convertStringToSlot(time24);
    return `${slot.hour}:${slot.minute} ${slot.period}`;
  }

  getTimeZoneLabel(id: string): string {
    switch (id) {
      case 'Arab Standard Time': return '(UTC+03:00) Kuwait, Riyadh';
      case 'UTC': return '(UTC) Coordinated Universal Time';
      case 'Bangladesh Standard Time': return '(UTC+06:00) Dhaka';
      default: return id || 'UTC';
    }
  }

  hasScheduleSet(): boolean {
    return this.scheduleData.times && this.scheduleData.times.length > 0;
  }

  openRefreshSchedule(): void {
    if (this.hasPermissionError) {
      this.toastr.warning(this.permissionMessage);
      return;
    }

    this.isLoading = true;
    this.powerBIEmbedService.getRefreshSchedule(this.activeDashboardType).subscribe({
      next: (schedule) => {
        this.isLoading = false;
        
        this.scheduleData = {
          days: schedule.days || [],
          times: schedule.times || [],
          localTimeZoneId: schedule.localTimeZoneId || 'Arab Standard Time',
          enabled: schedule.enabled ?? false,
          notifyOption: schedule.notifyOption || 'NoNotification'
        };

        if (this.scheduleData.days && this.scheduleData.days.length > 0 && this.scheduleData.days.length < 7) {
          this.refreshFrequency = 'Weekly';
        } else {
          this.refreshFrequency = 'Daily';
        }

        this.customEmails = localStorage.getItem(`refreshEmails_${this.tenantDomain}`) || '';
        this.notifyOthersEnabled = !!this.customEmails;

        this.timeSlots = (this.scheduleData.times || []).map((t: string) => this.convertStringToSlot(t));

        this.backupScheduleData = JSON.parse(JSON.stringify(this.scheduleData));
        this.isEditingSchedule = false;

        this.showRefreshModal = true;
      },
      error: (error) => {
        this.isLoading = false;
        console.error(error);
        this.toastr.error(error?.error || 'Could not fetch refresh schedule from Power BI');
      }
    });
  }

  startCreateSchedule(): void {
    this.scheduleData = {
      days: [...this.allDays],
      times: ['06:00'],
      localTimeZoneId: 'Arab Standard Time',
      enabled: true,
      notifyOption: 'NoNotification'
    };
    this.timeSlots = [{ hour: '06', minute: '00', period: 'AM' }];
    this.refreshFrequency = 'Daily';
    this.notifyOthersEnabled = false;
    this.customEmails = '';
    this.isEditingSchedule = true;
  }

  startEditSchedule(): void {
    this.isEditingSchedule = true;
  }

  discardEditSchedule(): void {
    this.scheduleData = JSON.parse(JSON.stringify(this.backupScheduleData));
    this.timeSlots = (this.scheduleData.times || []).map((t: string) => this.convertStringToSlot(t));
    
    if (this.scheduleData.days && this.scheduleData.days.length > 0 && this.scheduleData.days.length < 7) {
      this.refreshFrequency = 'Weekly';
    } else {
      this.refreshFrequency = 'Daily';
    }

    this.isEditingSchedule = false;
  }

  closeRefreshModal(): void {
    this.showRefreshModal = false;
    this.isEditingSchedule = false;
  }

  onFrequencyChange(): void {
    if (this.refreshFrequency === 'Daily') {
      this.scheduleData.days = [...this.allDays];
    } else {
      this.scheduleData.days = ['Monday'];
    }
  }

  isDaySelected(day: string): boolean {
    return this.scheduleData.days.includes(day);
  }

  toggleDay(day: string): void {
    const index = this.scheduleData.days.indexOf(day);
    if (index > -1) {
      this.scheduleData.days.splice(index, 1);
    } else {
      this.scheduleData.days.push(day);
    }
  }

  addTime(): void {
    if (this.timeSlots.length >= 8) {
      return;
    }
    this.timeSlots.push({ hour: '06', minute: '00', period: 'AM' });
  }

  removeTime(index: number): void {
    this.timeSlots.splice(index, 1);
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  applyRefreshSchedule(): void {
    if (this.scheduleData.enabled) {
      if (this.refreshFrequency === 'Daily') {
        this.scheduleData.days = [...this.allDays];
      } else if (this.refreshFrequency === 'Weekly') {
        if (!this.scheduleData.days || this.scheduleData.days.length === 0) {
          this.toastr.warning('Please select at least one day for weekly refresh.');
          return;
        }
      }

      if (!this.timeSlots || this.timeSlots.length === 0) {
        this.toastr.warning('Please add at least one time slot for the refresh schedule.');
        return;
      }
    }

    this.scheduleData.times = this.timeSlots.map(slot => this.convertSlotToString(slot));
    this.scheduleData.notifyOption = 'NoNotification';

    this.isLoading = true;

    if (this.notifyOthersEnabled && this.customEmails) {
      localStorage.setItem(`refreshEmails_${this.tenantDomain}`, this.customEmails);
    } else {
      localStorage.removeItem(`refreshEmails_${this.tenantDomain}`);
    }

    this.powerBIEmbedService.updateRefreshSchedule(this.scheduleData, this.activeDashboardType).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastr.success('Power BI dataset refresh schedule updated');
        this.backupScheduleData = JSON.parse(JSON.stringify(this.scheduleData));
        this.isEditingSchedule = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error(error);
        this.toastr.error(error?.error || 'Could not update refresh schedule');
      }
    });
  }

  downloadData(): void {
    if (this.hasPermissionError) {
      this.toastr.warning(this.permissionMessage);
      return;
    }

    this.isLoading = true;
    this.toastr.info('Generating well-formatted Excel report, please wait...');

    this.powerBIEmbedService.downloadFormattedExcel(this.activeDashboardType).subscribe({
      next: (blob: Blob) => {
        this.isLoading = false;
        
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `SmartPoultry_Report_${this.tenantDomain || 'Data'}_${this.activeDashboardType}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(blobUrl);
        
        this.toastr.success('Excel report downloaded successfully');
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Excel download failed:", error);
        this.toastr.error('Failed to download Excel report.');
      }
    });
  }

  // Admin Power BI Dataset Configuration Modal Methods
  openAdminSettingsModal(): void {
    this.isLoadingRolesConfig = true;
    this.showAdminSettingsModal = true;

    this.powerBIEmbedService.getRolesConfig().subscribe({
      next: (roles) => {
        this.isLoadingRolesConfig = false;
        this.rolesConfigList = (roles || []).map(r => ({
          roleId: r.roleId,
          roleName: r.roleName,
          operationalFabricId: r.operationalFabricId || '',
          financialFabricId: r.financialFabricId || '',
          executiveFabricId: r.executiveFabricId || ''
        }));
      },
      error: (err) => {
        this.isLoadingRolesConfig = false;
        this.toastr.error(err?.error || 'Could not load Power BI role configurations');
      }
    });
  }

  closeAdminSettingsModal(): void {
    this.showAdminSettingsModal = false;
  }

  saveAdminSettingsConfig(): void {
    this.isSavingRolesConfig = true;
    this.powerBIEmbedService.updateRolesConfig(this.rolesConfigList).subscribe({
      next: (res) => {
        this.isSavingRolesConfig = false;
        this.toastr.success(res?.message || 'Power BI dataset configuration updated successfully!');
        this.closeAdminSettingsModal();
        this.loadReport();
      },
      error: (err) => {
        this.isSavingRolesConfig = false;
        this.toastr.error(err?.error || 'Failed to update Power BI dataset configuration');
      }
    });
  }

  private async embedPowerBIReport(config: PowerBIEmbedConfig): Promise<void> {
    await this.ensurePowerBISdkLoaded();

    const powerbiClient = (window as any)['powerbi-client'];
    const powerbiService = (window as any).powerbi;
    const models = powerbiClient.models;

    this.resetPowerBIContainer();

    const reportConfig = {
      type: 'report',
      id: config.reportId,
      embedUrl: config.embedUrl,
      accessToken: config.embedToken,
      tokenType: models.TokenType.Embed,
      permissions: models.Permissions.Read,
      datasetBinding: {
        datasetId: config.datasetId
      },
      settings: {
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: false }
        },
        background: models.BackgroundType.Transparent,
        layoutType: models.LayoutType.Custom,
        customLayout: {
          displayOption: models.DisplayOption.FitToPage
        }
      }
    };

    this.report = powerbiService.embed(this.reportContainer.nativeElement, reportConfig);

    this.report.on('loaded', () => {
      console.log('[Smart Poultry] Power BI report loaded with dataset:', config.datasetId);
    });

    this.report.on('error', (event: any) => {
      console.error('[Smart Poultry] Power BI embed error:', event?.detail || event);
      this.toastr.error('Power BI report error. Check console for details.');
    });
  }

  private ensurePowerBISdkLoaded(): Promise<void> {
    if ((window as any).powerbi && (window as any)['powerbi-client']) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-powerbi-client="true"]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Power BI JavaScript SDK failed to load.')));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/powerbi-client@2/dist/powerbi.min.js';
      script.async = true;
      script.dataset['powerbiClient'] = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Power BI JavaScript SDK failed to load.'));
      document.body.appendChild(script);
    });
  }

  private resetPowerBIContainer(): void {
    const powerbiService = (window as any).powerbi;
    if (powerbiService && this.reportContainer?.nativeElement) {
      powerbiService.reset(this.reportContainer.nativeElement);
    }
    this.report = null;
  }
}
